import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, FloppyDisk, DeviceMobile, Trash, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PhoneFrame } from "@/components/common";
import { GuestView } from "@/components/guest/GuestView";
import { HostSection, PropertySection, StaySection, RatesSection, OfferSection, GuideSection, FormSection, ThemeSection } from "./editor/Sections";
import { ShareKit } from "./editor/ShareKit";
import { Housekeeping } from "./editor/Housekeeping";

const TABS = [["host", "Host", HostSection], ["property", "Property", PropertySection], ["stay", "Stay", StaySection], ["rates", "Rates", RatesSection], ["offer", "Offer", OfferSection], ["guide", "Guide", GuideSection], ["form", "Check-in form", FormSection], ["theme", "Theme", ThemeSection], ["share", "Share kit", null], ["housekeeping", "Housekeeping", null]];

function Preview({ d }) {
  const [stage, setStage] = useState("form");
  return (
    <div>
      <div className="mb-4 flex justify-center gap-1 rounded-full bg-secondary p-1 text-sm">
        {[["form", "Before check-in"], ["info", "After check-in"]].map(([k, l]) => (
          <button key={k} onClick={() => setStage(k)} data-testid={`preview-stage-${k}`} className={`rounded-full px-4 py-1.5 font-medium transition-colors duration-200 ${stage === k ? "bg-white shadow-sm" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      <PhoneFrame><GuestView property={d} info={d} previewStage={stage} onCheckin={() => {}} onReturning={() => {}} /></PhoneFrame>
    </div>
  );
}

export default function PropertyEditor() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [prop, setProp] = useState(null);
  const [d, setD] = useState(null);
  const [saved, setSaved] = useState("");
  const [tab, setTab] = useState("host");
  const [saving, setSaving] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);

  useEffect(() => {
    api.get(`/properties/${pid}`).then((r) => { setProp(r.data); setD(r.data.data); setSaved(JSON.stringify(r.data.data)); })
      .catch(() => { toast.error("Property not found"); nav("/dashboard"); });
  }, [pid, nav]);

  const dirty = useMemo(() => d && JSON.stringify(d) !== saved, [d, saved]);
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const up = (fn) => setD((cur) => { const c = structuredClone(cur); fn(c); return c; });

  const save = async () => {
    setSaving(true);
    try {
      const clean = structuredClone(d);
      clean.rules = clean.rules.map((r) => r.trim()).filter(Boolean);
      if (clean.housekeeping) clean.housekeeping = clean.housekeeping.map((r) => r.trim()).filter(Boolean);
      const r = await api.put(`/properties/${pid}`, { data: clean });
      setD(r.data.data);
      setSaved(JSON.stringify(r.data.data));
      toast.success("Saved. Your guest page is up to date.");
    } catch (e) { toast.error(errMsg(e, "Could not save")); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    await api.delete(`/properties/${pid}`);
    refresh();
    toast.success("Airbnb removed");
    nav("/dashboard");
  };

  if (!d) return <div className="flex items-center gap-2 text-muted-foreground"><SpinnerGap className="animate-spin" /> Loading editor…</div>;
  const Section = TABS.find((t) => t[0] === tab)[2];

  return (
    <div data-testid="property-editor">
      <div className="sticky top-0 z-30 -mx-5 mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-[#F9F7F4]/85 px-5 py-3 backdrop-blur-xl sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10">
        <div className="flex min-w-0 items-center gap-3">
          <Link to="/dashboard" data-testid="editor-back-link" className="grid h-9 w-9 place-items-center rounded-lg border border-border bg-white"><ArrowLeft /></Link>
          <div className="min-w-0"><h1 className="truncate font-display text-xl">{d.name || "Untitled"}</h1>
            <p className="text-xs" data-testid="editor-save-state">{dirty ? <span className="font-semibold text-[#C25D3E]">● Unsaved changes</span> : <span className="text-[#638C61]">✓ All changes saved</span>}</p></div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMobilePreview(true)} data-testid="mobile-preview-button" className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-white px-3 text-sm font-semibold xl:hidden"><DeviceMobile /> Preview</button>
          <AlertDialog>
            <AlertDialogTrigger asChild><button data-testid="delete-property-button" className="grid h-10 w-10 place-items-center rounded-lg border border-border bg-white text-muted-foreground hover:text-destructive"><Trash /></button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Remove this Airbnb?</AlertDialogTitle><AlertDialogDescription>The guest page and its key holder links will stop working. Guest contacts are kept.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel data-testid="delete-property-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} data-testid="delete-property-confirm" className="bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button onClick={save} disabled={!dirty || saving} data-testid="save-property-button" className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#D96C4A] px-5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#C25D3E] disabled:opacity-50">
            {saving ? <SpinnerGap className="animate-spin" /> : <FloppyDisk />} Save
          </button>
        </div>
      </div>

      <div className="grid gap-8 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-7">
          <div className="mb-5 flex flex-wrap gap-1">
            {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`editor-tab-${k}`} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${tab === k ? "bg-[#151312] text-white" : "text-muted-foreground hover:bg-secondary"}`}>{l}</button>)}
          </div>
          <div className="rise" key={tab}>{Section ? <Section d={d} up={up} property={prop} /> : tab === "share" ? <ShareKit property={prop} /> : <Housekeeping property={prop} d={d} up={up} />}</div>
        </div>
        <div className="hidden xl:col-span-5 xl:block"><div className="sticky top-24"><Preview d={d} /></div></div>
      </div>

      <Sheet open={mobilePreview} onOpenChange={setMobilePreview}>
        <SheetContent side="bottom" className="h-[92vh] overflow-y-auto bg-[#F9F7F4]" data-testid="mobile-preview-sheet">
          <SheetTitle className="mb-3">Live preview</SheetTitle>
          <Preview d={d} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
